/**
 * Shared search toolbar for search modals
 *
 * Renders: [Search input] [Filters button] [Sort slot] [Close button]
 * Used by both the Add Player modal and Global Search modal.
 * Styling follows Add Player's polished dark mode tokens.
 * @module components/shared/search-modal/SearchToolbar/SearchToolbarView
 */

import { ListFilterIcon, SearchIcon, XIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
// design-system: compound waiver — non-collapsible input composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: unavoidable primitive usage outside atoms (@/components/ui import(s): @/components/ui/input) —…
import { Input, InputField, InputIcon, InputSlot } from "@/components/ui/input";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Active state class for filter/sort buttons */
export const ACTIVE_BUTTON_CLASS =
  "h-10 flex-row items-center gap-2 rounded-sm border-2 border-accent-primary bg-accent-primary/15 px-5 dark:bg-accent-primary/20";

/** Default state class for filter/sort buttons */
export const DEFAULT_BUTTON_CLASS =
  "h-10 flex-row items-center gap-2 rounded-sm border border-outline-default px-5";

/** Props for SearchToolbarView */
interface SearchToolbarViewProps {
  /** Current search text */
  readonly searchQuery: string;
  /** Called when search text changes */
  readonly onSearchChange: (text: string) => void;
  /** Clear search text */
  readonly onClearSearch: () => void;
  /** Close the modal */
  readonly onClose: () => void;
  /** Number of active filters */
  readonly activeFilterCount: number;
  /** Toggle filter panel visibility */
  readonly onToggleFilterPanel: () => void;
  /** Slot for the sort trigger + dropdown — each modal renders its own */
  readonly sortTrigger: React.ReactNode;
  /**
   * Whether the facet controls belong on this host.
   *
   * True everywhere the toolbar has a modal's width to spend. FALSE on the
   * mobile add-player sheet, and the reason is measured rather than aesthetic:
   * this row is `[input flex-1] [Filters] [Sort] [Close]` inside `px-6 gap-3`,
   * and at sheet width the three buttons keep their intrinsic size while the
   * input — which has no width floor — collapses to a ~50px sliver that cannot
   * be typed into. Captured on an S24 (SE-7963).
   *
   * Frame 33 draws that sheet with a full-width input and no facet controls at
   * all, so hiding them is what the design asks for AND what makes the input
   * usable. A flag on the shared toolbar rather than a second toolbar: SE-7242
   * chose to share this body precisely so the sheet would not grow a parallel
   * implementation of it.
   */
  readonly showFacets?: boolean;

  /** Search input placeholder */
  readonly placeholder?: string;
  /** Test identity for the search field itself */
  readonly searchInputTestID?: string;
  /**
   * Accessible name for the search field.
   *
   * Gluestack's `InputField` names itself from `aria-label`; without one it
   * announces as "Input Field" and cannot be located by name. Optional so the
   * shipped callers are unchanged, and supplied by quick capture (SE-7242),
   * whose e2e locates this field by its accessible name.
   */
  readonly searchInputAccessibilityLabel?: string;
}

/**
 * Shared search toolbar with input, filters button, sort slot, and close button.
 * @param props - Component props
 * @param props.searchQuery - Current search text
 * @param props.onSearchChange - Handler for search text changes
 * @param props.onClearSearch - Clear search handler
 * @param props.onClose - Close modal handler
 * @param props.activeFilterCount - Number of active filters
 * @param props.onToggleFilterPanel - Toggle filter panel
 * @param props.sortTrigger - Sort trigger slot
 * @param props.showFacets - Whether Filters, Sort and Close belong on this host
 * @param props.placeholder - Search input placeholder
 * @param props.searchInputTestID - Test identity for the search field
 * @param props.searchInputAccessibilityLabel - Accessible name for the search field
 */
const SearchToolbarView = ({
  searchQuery,
  onSearchChange,
  onClearSearch,
  onClose,
  activeFilterCount,
  onToggleFilterPanel,
  sortTrigger,
  showFacets = true,
  placeholder = "Search any player...",
  searchInputTestID,
  searchInputAccessibilityLabel,
}: SearchToolbarViewProps) => (
  <Box className="relative z-50 border-b border-outline-default">
    <Stack
      direction="horizontal"
      className="items-center gap-3 overflow-visible px-6 py-3"
    >
      <Input
        variant="outline"
        className="web:transition-colors web:duration-150 web:hover:border-outline-strong h-10 flex-1 rounded-sm border-outline-strong data-[focus=true]:border-outline-strong"
        size="sm"
      >
        <InputSlot className="pl-3">
          <InputIcon
            as={SearchIcon}
            size="xs"
            className="text-content-subtle"
          />
        </InputSlot>
        <InputField
          placeholder={placeholder}
          className="web:outline-none text-title-sm text-content-secondary"
          value={searchQuery}
          onChangeText={onSearchChange}
          testID={searchInputTestID}
          aria-label={searchInputAccessibilityLabel}
        />
        {searchQuery.length > 0 ? (
          <InputSlot className="pr-3">
            <Pressable
              onPress={onClearSearch}
              accessibilityLabel="Clear search"
            >
              <Icon as={XIcon} size="sm" className="text-content-subtle" />
            </Pressable>
          </InputSlot>
        ) : null}
      </Input>

      {showFacets ? (
        <>
          <Pressable
            testID="player-filter:toggle"
            accessibilityLabel={`Filters${activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}`}
            onPress={onToggleFilterPanel}
            className={
              activeFilterCount > 0 ? ACTIVE_BUTTON_CLASS : DEFAULT_BUTTON_CLASS
            }
          >
            <Icon
              as={ListFilterIcon}
              size="sm"
              className={
                activeFilterCount > 0
                  ? "text-accent-primary"
                  : "text-content-muted"
              }
            />
            <Text
              variant="title-sm-medium"
              className={`${
                activeFilterCount > 0
                  ? "text-accent-primary"
                  : "text-content-muted"
              }`}
            >
              Filters
            </Text>
            {activeFilterCount > 0 ? (
              <Stack
                direction="horizontal"
                className="items-center justify-center rounded-sm bg-accent-primary px-1"
              >
                <Text
                  variant="micro-strong"
                  className="!text-content-on-accent"
                >
                  {activeFilterCount}
                </Text>
              </Stack>
            ) : null}
          </Pressable>

          {sortTrigger}

          <Pressable
            onPress={onClose}
            accessibilityLabel="Close modal"
            className="web:transition-colors web:duration-150 web:hover:bg-surface-subtle size-10 items-center justify-center rounded-none border border-outline-default"
          >
            <Icon as={XIcon} size="md" className="text-content-muted" />
          </Pressable>
        </>
      ) : null}
    </Stack>
  </Box>
);

SearchToolbarView.displayName = "SearchToolbarView";

export default memo(SearchToolbarView);
